import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Loader2, FileDown, Check, X, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, fmtXof, fmtDate, apiError, STATUS_LABELS, STATUS_STYLES, coverSrc } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StarRating } from "../components/StarRating";
import { CatalogTable } from "../components/admin/CatalogTable";
import { ProductForm } from "../components/admin/ProductForm";
import { CategoriesManager } from "../components/admin/CategoriesManager";
import { DevisManager } from "../components/admin/DevisManager";
import { FinanceManager } from "../components/admin/FinanceManager";
import { HRManager } from "../components/admin/HRManager";
import { VisioManager } from "../components/admin/VisioManager";
import { HospitalCareManager } from "../components/admin/HospitalCareManager";
import { ManagementManager } from "../components/admin/ManagementManager";
import { UsersManager } from "../components/admin/UsersManager";
import { ListesManager } from "../components/admin/ListesManager";
import { ThemeSelector } from "../components/admin/ThemeSelector";
import { CronJournal } from "../components/admin/CronJournal";
import { SoinsDocsManager } from "../components/admin/SoinsDocsManager";
import { PmsiModule } from "./PmsiCouverture";
import { DirectionModule } from "./DirectionGenerale";
import { LicensesManager } from "../components/admin/LicensesManager";
import { PlaquetteLeads } from "../components/admin/PlaquetteLeads";
import { DemosPipeline } from "../components/admin/DemosPipeline";
import { EmmyConversations } from "../components/admin/EmmyConversations";
import { ModuleToolbar } from "../components/ModuleToolbar";
import { AmbulanceManager } from "../components/admin/AmbulanceManager";
import { SatisfactionManager } from "../components/admin/SatisfactionManager";
import { AdsManager } from "../components/admin/AdsManager";
import { DriveManager } from "../components/admin/DriveManager";
import { AiModelSettings } from "../components/admin/AiModelSettings";
import { IntranetAdmin } from "../components/admin/IntranetAdmin";
import { WebinairesManager } from "../components/admin/WebinairesManager";
import { LmsManager } from "../components/admin/LmsManager";
import { CommercialDocs } from "../components/admin/CommercialDocs";
import { Messenger } from "../components/messaging/Messenger";

const SETTABLE_STATUSES = [
  ["confirmee", "Confirmée"], ["en_preparation", "En préparation"],
  ["expediee", "Expédiée"], ["livree", "Livrée"], ["annulee", "Annulée"],
];

function ConsolidatedKPIs() {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [f, h] = await Promise.all([api.get("/admin/finance/dashboard"), api.get("/admin/hr/dashboard")]);
        setD({ f: f.data, h: h.data });
      } catch (e) { /* widget masqué si indisponible */ }
    })();
  }, []);
  if (!d) return null;
  const items = [
    { label: "Bénéfice net", value: fmtXof(d.f.benefice), sub: `Marge ${d.f.marge} %`, color: d.f.benefice >= 0 ? "text-emerald-700" : "text-rose-600" },
    { label: "Trésorerie", value: fmtXof(d.f.tresorerie.solde_actuel), sub: "Solde actuel", color: d.f.tresorerie.solde_actuel >= 0 ? "text-emerald-700" : "text-rose-600" },
    { label: "Masse salariale", value: fmtXof(d.h.masse_salariale_mois), sub: "Mois en cours", color: "text-slate-800" },
    { label: "Effectif actif", value: d.h.actifs, sub: `${d.h.contrats_expirant.length} contrat(s) à échéance`, color: "text-slate-800", subAlert: d.h.contrats_expirant.length > 0 },
  ];
  return (
    <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="consolidated-overview">
      <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-700">Direction — vision 360° Finance &amp; RH</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl border border-emerald-100 bg-white p-4 shadow-sm" data-testid={`consolidated-kpi-${i}`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{it.label}</p>
            <p className={`mt-1.5 font-display text-lg font-bold ${it.color}`}>{it.value}</p>
            <p className={`mt-0.5 text-xs ${it.subAlert ? "font-bold text-rose-600" : "text-slate-500"}`}>{it.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-2 font-display text-2xl font-bold text-ciel">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState(new URLSearchParams(window.location.search).get("tab") || "apercu");
  const tabsRef = useRef(null);
  const activePanelNode = () => tabsRef.current?.querySelector('[role="tabpanel"][data-state="active"]') || tabsRef.current;
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promos, setPromos] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [formProduct, setFormProduct] = useState(null);
  const [promoDialog, setPromoDialog] = useState(false);
  const [promoForm, setPromoForm] = useState({ code: "", pct: 10, expires_at: "", max_uses: 100 });
  const [motifs, setMotifs] = useState({});
  const [report, setReport] = useState({ start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, p, o, pr, t, r, q, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/products"), api.get("/admin/orders"),
        api.get("/admin/promos"), api.get("/tiers"), api.get("/admin/refunds"), api.get("/admin/quotes"),
        api.get("/categories"),
      ]);
      setStats(s.data); setProducts(p.data); setOrders(o.data);
      setPromos(pr.data); setTiers(t.data); setRefunds(r.data); setQuotes(q.data); setCategories(c.data);
    } catch (e) { toast.error(apiError(e, "Erreur de chargement")); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePromo = async () => {
    setBusy(true);
    try {
      await api.post("/admin/promos", promoForm);
      toast.success("Code promo créé");
      setPromoDialog(false);
      setPromoForm({ code: "", pct: 10, expires_at: "", max_uses: 100 });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const togglePromo = async (p) => {
    try { await api.patch(`/admin/promos/${p.promo_id}`, { active: !p.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveTiers = async () => {
    try {
      await api.put("/admin/tiers", { tiers: tiers.filter((t) => t.qty > 0 && t.pct > 0) });
      toast.success("Paliers enregistrés");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeOrderStatus = async (orderId, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}/status`, { status });
      toast.success(`Statut mis à jour : ${data.label}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const decideRefund = async (refundId, action) => {
    try {
      await api.post(`/refunds/${refundId}/decision`, { action, motif: motifs[refundId] || "" });
      toast.success(action === "approuvee" ? "Remboursement approuvé" : "Demande rejetée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportReport = async (format) => {
    try {
      const res = await api.get(`/admin/reports?start=${report.start}&end=${report.end}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport-ventes-${report.start}_${report.end}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  if (!stats) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" size={28} /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-100">
    <div className="mx-auto max-w-7xl px-6 py-14" data-testid="admin-dashboard">
      <p className="font-mono text-xs uppercase tracking-widest text-ciel">Administration</p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold tracking-tight text-navy">Tableau de bord gestionnaire</h1>
        <ModuleToolbar getNode={activePanelNode} title={`gestionnaire-${activeTab}`}
          subfolder={activeTab === "finance" ? "Finances" : activeTab === "rh" ? "RH" : "Gestionnaire"} />
      </div>

      <Tabs ref={tabsRef} value={activeTab} onValueChange={setActiveTab} className="mt-10">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-slate-100 p-1">
          <TabsTrigger value="apercu" data-testid="tab-apercu">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="produits" data-testid="tab-produits">Catalogue Produits</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Catégories</TabsTrigger>
          <TabsTrigger value="commandes" data-testid="tab-commandes">Commandes</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promos">Codes promo</TabsTrigger>
          <TabsTrigger value="paliers" data-testid="tab-paliers">Paliers</TabsTrigger>
          <TabsTrigger value="remboursements" data-testid="tab-remboursements">Remboursements</TabsTrigger>
          <TabsTrigger value="devis" data-testid="tab-devis">Devis</TabsTrigger>
          <TabsTrigger value="rapports" data-testid="tab-rapports">Rapports</TabsTrigger>
          <TabsTrigger value="drive" data-testid="tab-drive">Google Drive</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance">Gestion Financière</TabsTrigger>
          <TabsTrigger value="rh" data-testid="tab-rh">Ressources Humaines</TabsTrigger>
          <TabsTrigger value="utilisateurs" data-testid="tab-utilisateurs">Utilisateurs & rôles</TabsTrigger>
          <TabsTrigger value="listes" data-testid="tab-listes">Configuration des listes</TabsTrigger>
          <TabsTrigger value="apparence" data-testid="tab-apparence">Apparence</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal">Journal des envois</TabsTrigger>
          <TabsTrigger value="visio" data-testid="tab-visio">Visioconférence</TabsTrigger>
          <TabsTrigger value="soins" data-testid="tab-soins">Soins hospitaliers</TabsTrigger>
          <TabsTrigger value="pmsi" data-testid="tab-pmsi">PMSI & Couverture</TabsTrigger>
          <TabsTrigger value="direction" data-testid="tab-direction">Direction Générale</TabsTrigger>
          <TabsTrigger value="pilotage" data-testid="tab-pilotage">Management stratégique</TabsTrigger>
          <TabsTrigger value="licences" data-testid="tab-licences">Gestion des licences</TabsTrigger>
          <TabsTrigger value="prospects" data-testid="tab-prospects">Prospects plaquette</TabsTrigger>
          <TabsTrigger value="demos" data-testid="tab-demos">Suivi des démos</TabsTrigger>
          <TabsTrigger value="emmy" data-testid="tab-emmy">Conversations Emmy</TabsTrigger>
          <TabsTrigger value="ambulances" data-testid="tab-ambulances">Ambulances et mobilité</TabsTrigger>
          <TabsTrigger value="gestion" data-testid="tab-gestion">Gestion médicale & scientifique</TabsTrigger>
          <TabsTrigger value="satisfaction" data-testid="tab-satisfaction">Satisfaction du patient</TabsTrigger>
          <TabsTrigger value="pub" data-testid="tab-pub">Publicités</TabsTrigger>
          <TabsTrigger value="parametres-ia" data-testid="tab-parametres-ia">Paramètres IA</TabsTrigger>
          <TabsTrigger value="intranet" data-testid="tab-intranet">Messagerie intranet</TabsTrigger>
          <TabsTrigger value="webinaires" data-testid="tab-webinaires">Webinaires</TabsTrigger>
          <TabsTrigger value="lms" data-testid="tab-lms">Formations (LMS)</TabsTrigger>
          <TabsTrigger value="commercial" data-testid="tab-commercial">Documents commerciaux</TabsTrigger>
        </TabsList>

        {/* VUE D'ENSEMBLE */}
        <TabsContent value="apercu" className="mt-6">
          <ConsolidatedKPIs />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Ventes du jour" value={stats.ventes_jour} sub={`${fmtXof(stats.revenus_jour)} aujourd'hui`} testid="stat-sales-today" />
            <StatCard label="Revenus totaux" value={fmtXof(stats.revenus_total)} sub={`${stats.commandes_total} commandes`} testid="stat-revenue" />
            <StatCard label="Clients" value={stats.clients} testid="stat-clients" />
            <StatCard label="Remboursements en attente" value={stats.remboursements_en_attente} testid="stat-refunds" />
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="best-sellers">
              <h3 className="font-display text-base font-semibold text-ciel">Produits les plus vendus</h3>
              {stats.best_sellers.length === 0 ? <p className="mt-4 text-sm text-slate-400">Aucune vente pour le moment.</p> : (
                <ul className="mt-4 space-y-3">
                  {stats.best_sellers.map((b, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 text-sm">
                      <span className="flex items-center gap-3 text-slate-700">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-navy font-mono text-[10px] font-bold text-white">{i + 1}</span>
                        {b.title}
                      </span>
                      <span className="whitespace-nowrap font-mono text-xs text-slate-500">{b.qty} vendus · {fmtXof(b.revenue)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="recent-reviews">
              <h3 className="font-display text-base font-semibold text-ciel">Évaluations récentes</h3>
              {stats.avis_recents.length === 0 ? <p className="mt-4 text-sm text-slate-400">Aucun avis pour le moment.</p> : (
                <ul className="mt-4 space-y-3">
                  {stats.avis_recents.slice(0, 5).map((r) => (
                    <li key={r.review_id} className="text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700">{r.client_name}</span>
                        <StarRating value={r.rating} size={12} />
                      </div>
                      {r.comment && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{r.comment}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {stats.devise_stats && Object.keys(stats.devise_stats).length > 0 && (
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="currency-stats">
              <h3 className="font-display text-base font-semibold text-ciel">Répartition des ventes par devise</h3>
              <p className="mt-1 text-xs text-slate-500">
                Total converti en FCFA : <strong className="text-navy">{fmtXof(Object.values(stats.devise_stats).reduce((s, d) => s + d.total_xof, 0))}</strong>
                {stats.devise_preferee && <> · Devise la plus utilisée : <strong className="text-navy">{stats.devise_preferee}</strong></>}
              </p>
              <div className="mt-4 space-y-2.5">
                {Object.entries(stats.devise_stats).map(([cur, d]) => {
                  const max = Math.max(...Object.values(stats.devise_stats).map((x) => x.total_xof));
                  return (
                    <div key={cur} className="flex items-center gap-3" data-testid={`currency-stat-${cur}`}>
                      <span className="w-12 font-mono text-xs font-semibold text-navy">{cur}</span>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-2.5 rounded-full bg-ciel transition-all duration-500" style={{ width: `${Math.max(4, (d.total_xof / max) * 100)}%` }} />
                      </div>
                      <span className="whitespace-nowrap font-mono text-xs text-slate-500">{d.count} vente(s) · {fmtXof(d.total_xof)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {stats.whatsapp_stats && (
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="whatsapp-stats">
              <h3 className="font-display text-base font-semibold text-ciel">Partages WhatsApp</h3>
              <p className="mt-1 text-xs text-slate-500">
                7 derniers jours : <strong className="text-navy">{stats.whatsapp_stats.total_7j}</strong> · 30 derniers jours : <strong className="text-navy">{stats.whatsapp_stats.total_30j}</strong>
                {stats.whatsapp_stats.plus_partage && <> · Produit le plus partagé : <strong className="text-navy">{stats.whatsapp_stats.plus_partage.title}</strong> ({stats.whatsapp_stats.plus_partage.count})</>}
              </p>
              {stats.whatsapp_stats.par_produit.length > 0 && (
                <div className="mt-4 space-y-2.5">
                  {stats.whatsapp_stats.par_produit.map((w) => {
                    const max = stats.whatsapp_stats.par_produit[0].count;
                    return (
                      <div key={w.product_id} className="flex items-center gap-3" data-testid={`wa-share-${w.product_id}`}>
                        <span className="max-w-[320px] truncate text-xs text-slate-600">{w.title}</span>
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-2.5 rounded-full transition-all duration-500" style={{ width: `${Math.max(4, (w.count / max) * 100)}%`, backgroundColor: "#25D366" }} />
                        </div>
                        <span className="font-mono text-xs font-semibold text-navy">{w.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="promo-stats">
            <h3 className="font-display text-base font-semibold text-ciel">Utilisation des codes promo</h3>
            {stats.promo_stats.length === 0 ? <p className="mt-4 text-sm text-slate-400">Aucun code promo créé.</p> : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
                    <th className="pb-2">Code</th><th className="pb-2">Réduction</th><th className="pb-2">Utilisations</th><th className="pb-2">Remise totale</th><th className="pb-2">Statut</th>
                  </tr></thead>
                  <tbody>
                    {stats.promo_stats.map((p) => (
                      <tr key={p.code} className="border-b border-slate-100">
                        <td className="py-2.5 font-mono font-semibold text-navy">{p.code}</td>
                        <td className="py-2.5">-{p.pct}%</td>
                        <td className="py-2.5 font-mono">{p.uses}/{p.max_uses}</td>
                        <td className="py-2.5 font-mono">{fmtXof(p.remise_totale)}</td>
                        <td className="py-2.5">{p.active ? <span className="text-emerald-600">Actif</span> : <span className="text-slate-400">Inactif</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* GESTION DU CATALOGUE */}
        <TabsContent value="produits" className="mt-6">
          <CatalogTable
            products={products}
            categories={categories}
            onReload={load}
            onNew={() => { setFormProduct(null); setFormOpen(true); }}
            onEdit={(p) => { setFormProduct(p); setFormOpen(true); }}
          />
        </TabsContent>

        {/* CATÉGORIES */}
        <TabsContent value="categories" className="mt-6">
          <CategoriesManager onChanged={load} />
        </TabsContent>

        {/* COMMANDES */}
        <TabsContent value="commandes" className="mt-6">
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="orders-table">
            <table className="w-full min-w-[720px] text-sm">
              <thead><tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
                <th className="p-4">Commande</th><th className="p-4">Date</th><th className="p-4">Client</th><th className="p-4">Produits</th><th className="p-4">Total</th><th className="p-4">Statut</th>
              </tr></thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-400">Aucune commande.</td></tr>}
                {orders.map((o) => {
                  const hasPrestation = o.items.some((it) => it.offer_type === "prestation");
                  const isOpen = expandedOrder === o.order_id;
                  return (
                  <React.Fragment key={o.order_id}>
                  <tr className="border-b border-slate-100" data-testid={`admin-order-${o.order_id}`}>
                    <td className="p-4 font-mono font-semibold text-navy">{o.order_id}</td>
                    <td className="p-4 text-xs text-slate-500">{fmtDate(o.created_at)}</td>
                    <td className="p-4 text-xs text-slate-600">{o.client_email}</td>
                    <td className="max-w-[220px] p-4 text-xs text-slate-500">{o.items.map((i) => i.title).join(", ")}</td>
                    <td className="p-4 font-mono font-semibold">{fmtXof(o.total)}</td>
                    <td className="p-4">
                      <div className="flex flex-col items-start gap-1.5">
                        <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${STATUS_STYLES[o.status]}`}>{STATUS_LABELS[o.status] || o.status}</span>
                        <select value="" onChange={(e) => { if (e.target.value) changeOrderStatus(o.order_id, e.target.value); }}
                          data-testid={`order-status-select-${o.order_id}`}
                          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[10px] font-medium text-slate-600 hover:border-ciel">
                          <option value="">Changer le statut…</option>
                          {SETTABLE_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        {hasPrestation && (
                          <button onClick={() => setExpandedOrder(isOpen ? null : o.order_id)} data-testid={`manage-milestones-${o.order_id}`}
                            className="rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100">
                            {isOpen ? "Masquer les jalons" : "Voir les jalons"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {hasPrestation && isOpen && (
                    <tr className="border-b border-slate-100 bg-emerald-50/30" data-testid={`milestones-panel-${o.order_id}`}>
                      <td colSpan={6} className="p-4">
                        {o.items.filter((it) => it.offer_type === "prestation").map((it) => (
                          <div key={it.product_id} className="mb-3 last:mb-0">
                            <p className="mb-2 text-xs font-semibold text-navy">{it.title}</p>
                            <div className="flex flex-wrap gap-2">
                              {(it.milestones || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((m) => (
                                <span key={m.key} data-testid={`milestone-status-${o.order_id}-${m.key}`}
                                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold ${m.paid ? "border-emerald-400 bg-emerald-100 text-emerald-700" : "border-slate-300 bg-white text-slate-500"}`}>
                                  {m.paid ? <Check size={13} /> : <span className="h-3 w-3 rounded-full border border-slate-400" />}
                                  {m.label} · {fmtXof(m.amount || 0)} · {m.paid ? "Payé" : "En attente"}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                        <p className="mt-1 text-[11px] text-slate-500">Les jalons sont réglés en ligne par le client (carte ou Mobile Money) ; le déblocage des livrables est automatique.</p>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <Messenger mode="vendor" />
        </TabsContent>

        {/* CODES PROMO */}
        <TabsContent value="promos" className="mt-6">
          <div className="mb-5 flex justify-end">
            <Button data-testid="new-promo-btn" onClick={() => setPromoDialog(true)} className="rounded-full bg-ciel text-white hover:bg-sky-700">
              <Plus size={15} className="mr-1.5" /> Nouveau code
            </Button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="promos-table">
            <table className="w-full min-w-[680px] text-sm">
              <thead><tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
                <th className="p-4">Code</th><th className="p-4">Réduction</th><th className="p-4">Expiration</th><th className="p-4">Utilisations</th><th className="p-4">Actif</th>
              </tr></thead>
              <tbody>
                {promos.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-400">Aucun code promo.</td></tr>}
                {promos.map((p) => (
                  <tr key={p.promo_id} className="border-b border-slate-100" data-testid={`promo-row-${p.code}`}>
                    <td className="p-4 font-mono font-semibold text-navy">{p.code}</td>
                    <td className="p-4">-{p.pct}%</td>
                    <td className="p-4 text-xs text-slate-500">{fmtDate(p.expires_at)}</td>
                    <td className="p-4 font-mono">{p.uses}/{p.max_uses}</td>
                    <td className="p-4"><Switch checked={p.active} onCheckedChange={() => togglePromo(p)} data-testid={`toggle-promo-${p.code}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* PALIERS */}
        <TabsContent value="paliers" className="mt-6">
          <div className="max-w-xl rounded-xl border border-slate-200 bg-white p-6" data-testid="tiers-editor">
            <h3 className="font-display text-base font-semibold text-ciel">Tarifs dégressifs — Applications informatiques</h3>
            <p className="mt-1 text-xs text-slate-500">Remise automatique selon le nombre de licences. Cumulable avec les codes promo.</p>
            <div className="mt-5 space-y-3">
              {tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Input type="number" min={1} value={t.qty} data-testid={`tier-qty-${i}`} className="w-24 font-mono"
                    onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, qty: parseInt(e.target.value) || 0 } : x))} />
                  <span className="text-sm text-slate-500">licences et +</span>
                  <Input type="number" min={1} max={90} value={t.pct} data-testid={`tier-pct-${i}`} className="w-24 font-mono"
                    onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, pct: parseInt(e.target.value) || 0 } : x))} />
                  <span className="text-sm text-slate-500">%</span>
                  <button onClick={() => setTiers(tiers.filter((_, j) => j !== i))} data-testid={`tier-remove-${i}`} className="text-slate-400 hover:text-red-500"><X size={16} /></button>
                </div>
              ))}
            </div>
            <div className="mt-5 flex gap-3">
              <Button variant="outline" data-testid="tier-add" onClick={() => setTiers([...tiers, { qty: 20, pct: 30 }])}><Plus size={14} className="mr-1" /> Ajouter un palier</Button>
              <Button data-testid="tiers-save" onClick={saveTiers} className="bg-navy text-white hover:bg-navy-light">Enregistrer</Button>
            </div>
          </div>
        </TabsContent>

        {/* REMBOURSEMENTS */}
        <TabsContent value="remboursements" className="mt-6">
          {refunds.length === 0 ? (
            <p className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="no-refunds-admin">Aucune demande de remboursement.</p>
          ) : (
            <div className="space-y-4">
              {refunds.map((r) => (
                <div key={r.refund_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`admin-refund-${r.refund_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-mono text-sm font-semibold text-navy">{r.order_id} · {fmtXof(r.amount)}</p>
                      <p className="text-xs text-slate-500">{r.client_name} · {fmtDate(r.created_at)} · {r.order_items.join(", ")}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
                      r.status === "approuvee" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : r.status === "rejetee" ? "border-red-300 bg-red-50 text-red-700"
                      : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                      {r.status === "approuvee" ? "Approuvée" : r.status === "rejetee" ? "Rejetée" : "En attente"}
                    </span>
                  </div>
                  <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{r.reason}</p>
                  {r.attachment && (
                    <a href={coverSrc(r.attachment)} target="_blank" rel="noreferrer" data-testid={`admin-refund-attachment-${r.refund_id}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-ciel hover:underline">
                      <Paperclip size={12} /> Justificatif joint par le client
                    </a>
                  )}
                  {r.status === "en_attente" && (
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <Input
                        data-testid={`refund-motif-${r.refund_id}`}
                        value={motifs[r.refund_id] || ""}
                        onChange={(e) => setMotifs({ ...motifs, [r.refund_id]: e.target.value })}
                        placeholder="Motif de la décision (optionnel)…"
                        className="max-w-sm"
                      />
                      <Button data-testid={`refund-approve-${r.refund_id}`} onClick={() => decideRefund(r.refund_id, "approuvee")} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700">
                        <Check size={14} className="mr-1" /> Approuver
                      </Button>
                      <Button data-testid={`refund-reject-${r.refund_id}`} onClick={() => decideRefund(r.refund_id, "rejetee")} variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50">
                        <X size={14} className="mr-1" /> Refuser
                      </Button>
                    </div>
                  )}
                  {r.motif && <p className="mt-2 text-xs text-slate-400">Motif : {r.motif}</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* DEVIS ÉTABLISSEMENTS */}
        <TabsContent value="devis" className="mt-6">
          <DevisManager />
          {quotes.length > 0 && (
            <div className="mt-10">
              <h3 className="font-display text-base font-semibold text-ciel">Demandes établissements (formulaire produit)</h3>
              <div className="mt-4 space-y-4">
              {quotes.map((q) => (
                <div key={q.quote_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`quote-row-${q.quote_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-display text-sm font-semibold text-navy">{q.establishment}</p>
                      <p className="text-xs text-slate-500">{q.contact_name} · {q.email} · {q.phone} · {fmtDate(q.created_at)}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
                      q.status === "traite" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : q.status === "en_cours" ? "border-sky-300 bg-sky-50 text-ciel"
                      : "border-amber-300 bg-amber-50 text-amber-700"}`} data-testid={`quote-status-${q.quote_id}`}>
                      {q.status === "traite" ? "Traité" : q.status === "en_cours" ? "En cours" : "Nouveau"}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    <span className="font-medium">{q.product_title}</span> — <span className="font-mono">{q.qty} licence(s)</span>
                  </p>
                  {q.message && <p className="mt-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">{q.message}</p>}
                  {q.status !== "traite" && (
                    <div className="mt-3 flex gap-2">
                      {q.status === "nouveau" && (
                        <Button size="sm" variant="outline" data-testid={`quote-progress-${q.quote_id}`}
                          onClick={async () => { await api.post(`/admin/quotes/${q.quote_id}/status`, { status: "en_cours" }); load(); }}
                          className="rounded-full">Marquer en cours</Button>
                      )}
                      <Button size="sm" data-testid={`quote-done-${q.quote_id}`}
                        onClick={async () => { await api.post(`/admin/quotes/${q.quote_id}/status`, { status: "traite" }); load(); }}
                        className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700">Marquer traité</Button>
                    </div>
                  )}
                </div>
              ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* RAPPORTS */}
        <TabsContent value="rapports" className="mt-6">
          <div className="max-w-xl rounded-xl border border-slate-200 bg-white p-6" data-testid="reports-panel">
            <h3 className="font-display text-base font-semibold text-ciel">Exporter les ventes par période</h3>
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-500">Début</label>
                <Input type="date" value={report.start} data-testid="report-start" className="mt-1.5"
                  onChange={(e) => setReport({ ...report, start: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Fin</label>
                <Input type="date" value={report.end} data-testid="report-end" className="mt-1.5"
                  onChange={(e) => setReport({ ...report, end: e.target.value })} />
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <Button data-testid="export-pdf" onClick={() => exportReport("pdf")} className="rounded-full bg-navy text-white hover:bg-navy-light">
                <FileDown size={14} className="mr-1.5" /> PDF
              </Button>
              <Button data-testid="export-xlsx" onClick={() => exportReport("xlsx")} variant="outline" className="rounded-full">
                <FileDown size={14} className="mr-1.5" /> Excel
              </Button>
            </div>
          </div>
        </TabsContent>
        {/* GESTION FINANCIÈRE */}
        <TabsContent value="finance" className="mt-6">
          <FinanceManager />
        </TabsContent>

        {/* RESSOURCES HUMAINES */}
        <TabsContent value="rh" className="mt-6">
          <HRManager />
        </TabsContent>

        {/* UTILISATEURS & RÔLES */}
        <TabsContent value="utilisateurs" className="mt-6">
          <UsersManager />
        </TabsContent>

        {/* CONFIGURATION DES LISTES (secteurs / services) */}
        <TabsContent value="listes" className="mt-6">
          <ListesManager />
        </TabsContent>

        {/* APPARENCE (thème global) */}
        <TabsContent value="apparence" className="mt-6">
          <ThemeSelector />
        </TabsContent>

        {/* JOURNAL DES ENVOIS (crons & emails) */}
        <TabsContent value="journal" className="mt-6">
          <CronJournal />
        </TabsContent>

        {/* VISIOCONFÉRENCE */}
        <TabsContent value="visio" className="mt-6">
          <VisioManager />
        </TabsContent>

        <TabsContent value="soins" className="mt-6">
          <Tabs defaultValue="analytique">
            <TabsList className="mb-4 bg-slate-100">
              <TabsTrigger value="analytique" data-testid="soins-tab-analytique">Analytique</TabsTrigger>
              <TabsTrigger value="documents" data-testid="soins-tab-documents">Certificats médicaux</TabsTrigger>
            </TabsList>
            <TabsContent value="analytique">
              <HospitalCareManager module="soins" title="Résultat des soins — Soins médicaux hospitaliers" />
            </TabsContent>
            <TabsContent value="documents">
              <SoinsDocsManager />
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="pmsi" className="mt-6">
          <PmsiModule embedded />
        </TabsContent>

        <TabsContent value="direction" className="mt-6">
          <DirectionModule embedded />
        </TabsContent>

        <TabsContent value="pilotage" className="mt-6">
          <ManagementManager />
        </TabsContent>

        <TabsContent value="licences" className="mt-6">
          <LicensesManager />
        </TabsContent>

        <TabsContent value="prospects" className="mt-6">
          <PlaquetteLeads />
        </TabsContent>

        <TabsContent value="demos" className="mt-6">
          <DemosPipeline />
        </TabsContent>

        <TabsContent value="emmy" className="mt-6">
          <EmmyConversations />
        </TabsContent>

        <TabsContent value="ambulances" className="mt-6">
          <AmbulanceManager />
        </TabsContent>

        <TabsContent value="gestion" className="mt-6">
          <HospitalCareManager module="gestionnaire" title="Résultat des soins — Gestion médicale & scientifique" />
        </TabsContent>

        <TabsContent value="satisfaction" className="mt-6">
          <SatisfactionManager />
        </TabsContent>

        <TabsContent value="pub" className="mt-6">
          <AdsManager />
        </TabsContent>

        <TabsContent value="drive" className="mt-6">
          <DriveManager />
        </TabsContent>

        <TabsContent value="parametres-ia" className="mt-6">
          <AiModelSettings />
        </TabsContent>

        <TabsContent value="intranet" className="mt-6">
          <IntranetAdmin />
        </TabsContent>

        <TabsContent value="webinaires" className="mt-6">
          <WebinairesManager />
        </TabsContent>

        <TabsContent value="lms" className="mt-6">
          <LmsManager />
        </TabsContent>

        <TabsContent value="commercial" className="mt-6">
          <CommercialDocs />
        </TabsContent>
      </Tabs>

      <ProductForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        product={formProduct}
        categories={categories}
        onSaved={load}
      />


      {/* Dialog code promo */}
      <Dialog open={promoDialog} onOpenChange={setPromoDialog}>
        <DialogContent data-testid="promo-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Nouveau code promo</DialogTitle></DialogHeader>
          <div className="mt-2 space-y-4">
            <Input data-testid="promo-code" placeholder="CODE (ex : SANTE20)" value={promoForm.code} className="font-mono uppercase"
              onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-500">Réduction (%)</label>
                <Input data-testid="promo-pct" type="number" min={1} max={90} value={promoForm.pct} className="mt-1.5"
                  onChange={(e) => setPromoForm({ ...promoForm, pct: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Utilisations max</label>
                <Input data-testid="promo-max" type="number" min={1} value={promoForm.max_uses} className="mt-1.5"
                  onChange={(e) => setPromoForm({ ...promoForm, max_uses: parseInt(e.target.value) || 1 })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Date d'expiration</label>
              <Input data-testid="promo-expires" type="date" value={promoForm.expires_at} className="mt-1.5"
                onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value })} />
            </div>
            <Button data-testid="promo-save" onClick={savePromo} disabled={busy || promoForm.code.length < 3 || !promoForm.expires_at}
              className="w-full rounded-full bg-ciel py-5 text-sm font-semibold text-white hover:bg-sky-700">
              {busy ? "Création…" : "Créer le code"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}
